import { WORK_DIR } from '~/utils/constants';
import type { FileMap } from '~/lib/stores/files';

/**
 * browser-metro's file entry shape. Kept as a local type so this module does
 * not need to import from `browser-metro` (which only exists in the worker
 * bundle) — the shape is `{ content, isExternal }`.
 */
export interface BrowserMetroFileEntry {
  content: string;
  isExternal: boolean;
}

export type BrowserMetroFiles = Record<string, BrowserMetroFileEntry>;

// Extensions dualite persists as base64 (see FilesStore / action-runner). For
// browser-metro these are passed through as data-URIs so `require('./logo.png')`
// resolves to something instead of being silently dropped.
const BINARY_MIME_BY_EXT: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  ico: 'image/x-icon',
  svg: 'image/svg+xml',
  avif: 'image/avif',
  ttf: 'font/ttf',
  otf: 'font/otf',
  woff: 'font/woff',
  woff2: 'font/woff2',
  eot: 'application/vnd.ms-fontobject',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  m4a: 'audio/mp4',
  ogg: 'audio/ogg',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
};

function extensionOf(path: string): string {
  const dot = path.lastIndexOf('.');

  if (dot === -1) {
    return '';
  }

  return path.slice(dot + 1).toLowerCase();
}

/**
 * Convert a dualite WebContainer path to the leading-slash, project-relative
 * path browser-metro expects.
 *
 *   /home/project/app/index.tsx  ->  /app/index.tsx
 *   /home/project/package.json   ->  /package.json
 */
export function toBrowserMetroPath(path: string): string {
  let relative = path;

  if (relative.startsWith(WORK_DIR)) {
    relative = relative.slice(WORK_DIR.length);
  }

  if (!relative.startsWith('/')) {
    relative = '/' + relative;
  }

  return relative;
}

/**
 * Convert dualite's `FileMap` (the workbench's single source of truth) into the
 * `{ path: { content, isExternal } }` map browser-metro's `VirtualFS` consumes.
 *
 * - strips the `WORK_DIR` (`/home/project`) prefix and emits leading-slash paths
 * - keeps only `type: 'file'` entries (folders and `undefined` holes are dropped)
 * - text files pass through verbatim; binary files (`isBinary`) are emitted as
 *   base64 data-URIs so asset `require()`s still resolve instead of vanishing
 */
export function toBrowserMetroFiles(files: FileMap): BrowserMetroFiles {
  const out: BrowserMetroFiles = {};

  for (const [path, dirent] of Object.entries(files)) {
    if (!dirent || dirent.type !== 'file') {
      continue;
    }

    const relativePath = toBrowserMetroPath(path);

    if (dirent.isBinary) {
      const mime = BINARY_MIME_BY_EXT[extensionOf(relativePath)] || 'application/octet-stream';

      // dualite stores binary file content as base64 already.
      out[relativePath] = {
        content: `data:${mime};base64,${dirent.content}`,
        isExternal: false,
      };
      continue;
    }

    out[relativePath] = { content: dirent.content, isExternal: false };
  }

  return out;
}
